/**
 * Apple Calendar background sync utility.
 *
 * Reads device EventKit calendars via expo-calendar, performs a delta sync
 * against the backend (only events modified since the last sync timestamp),
 * and sends them in batches of 50.  Fires a local push notification when
 * the sync completes so the user gets feedback even when backgrounded.
 *
 * Background task follows the same pattern as healthBackgroundTask.ts:
 *   - Lazy-load expo-task-manager + expo-background-task (avoids crash in Expo Go)
 *   - defineTask MUST be called at the top level (before AppRegistry)
 *   - minimumInterval: 1 hour (calendar changes are not time-critical)
 *
 * IMPORTANT: Import this file from index.ts so defineTask runs at startup.
 */

import { Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import * as Notifications from 'expo-notifications';
import { store } from '../store';
import { syncAppleCalendar } from '../store/actions/calendar';
import debug from './debug';

export const APPLE_CALENDAR_SYNC_TASK = 'ELEPLAN_APPLE_CALENDAR_SYNC';
export const APPLE_CAL_LAST_SYNC_KEY = (projectId: string) =>
  `@eleplan/apple_cal_last_sync_${projectId}`;

/**
 * Per-project list of EventKit calendar IDs the user explicitly chose to sync.
 * Written as a JSON-stringified string[]. When absent, the sync path falls
 * back to "all calendars" for backwards-compat with installs that connected
 * before the picker existed.
 */
export const APPLE_CAL_SELECTED_IDS_KEY = (projectId: string) =>
  `@eleplan/apple_cal_selected_ids_${projectId}`;

// ── Lazy-load native modules ──────────────────────────────────────────────────

let TaskManager: typeof import('expo-task-manager') | null = null;
let BackgroundTask: typeof import('expo-background-task') | null = null;

if (Platform.OS !== 'web') {
  try {
    TaskManager = require('expo-task-manager') as typeof import('expo-task-manager');
    BackgroundTask = require('expo-background-task') as typeof import('expo-background-task');
  } catch {
    // Native modules not compiled in — background Apple Calendar sync disabled.
  }
}

// ── Task definition (must be top-level) ──────────────────────────────────────

if (TaskManager && BackgroundTask) {
  const _bt = BackgroundTask;
  try {
    TaskManager.defineTask(APPLE_CALENDAR_SYNC_TASK, async () => {
      try {
        debug.log('ui', 'Background Apple Calendar sync task started');
        await runAppleCalendarSyncAllProjects();
        debug.log('ui', 'Background Apple Calendar sync task completed');
        return _bt.BackgroundTaskResult.Success;
      } catch (err) {
        debug.warn('Background Apple Calendar sync task failed', err);
        return _bt.BackgroundTaskResult.Failed;
      }
    });
  } catch {
    // defineTask failed — likely Expo Go without native modules.
  }
}

// ── Registration helpers ──────────────────────────────────────────────────────

export async function registerAppleCalendarTask(): Promise<void> {
  if (Platform.OS !== 'ios' || !BackgroundTask || !TaskManager) return;
  try {
    const status = await BackgroundTask.getStatusAsync();
    if (
      status === BackgroundTask.BackgroundTaskStatus.Restricted
    ) {
      // System-level "no background tasks allowed" (sim, or Background App
      // Refresh disabled). Expected, not actionable — log at info instead
      // of warn to keep the dev console quiet across re-launches.
      debug.info('background-task', 'Apple Calendar background task unavailable — skipping registration', { status });
      return;
    }
    const alreadyRegistered = await TaskManager.isTaskRegisteredAsync(APPLE_CALENDAR_SYNC_TASK);
    if (alreadyRegistered) return;
    await BackgroundTask.registerTaskAsync(APPLE_CALENDAR_SYNC_TASK, {
      minimumInterval: 60 * 60, // 1 hour
    });
    debug.log('ui', 'Apple Calendar background task registered');
  } catch (err) {
    debug.warn('Failed to register Apple Calendar background task', err);
  }
}

export async function unregisterAppleCalendarTask(): Promise<void> {
  if (Platform.OS !== 'ios' || !BackgroundTask || !TaskManager) return;
  try {
    const registered = await TaskManager.isTaskRegisteredAsync(APPLE_CALENDAR_SYNC_TASK);
    if (registered) {
      await BackgroundTask.unregisterTaskAsync(APPLE_CALENDAR_SYNC_TASK);
      debug.log('ui', 'Apple Calendar background task unregistered');
    }
  } catch (err) {
    debug.warn('Failed to unregister Apple Calendar background task', err);
  }
}

// ── Core sync helpers ─────────────────────────────────────────────────────────

/**
 * Sync all projects that have an active Apple Calendar token.
 * Called from the background task and on app foreground.
 */
async function runAppleCalendarSyncAllProjects(): Promise<void> {
  const state = store.getState();
  const projects: any[] = Object.values((state as any).projects?.list ?? {});
  for (const project of projects) {
    const projectId = String(project.id ?? project.project_id);
    if (!projectId) continue;
    try {
      await (store.dispatch as any)(syncAppleCalendar(projectId));
    } catch (err) {
      debug.warn('Apple Calendar sync failed for project', { projectId, err });
    }
  }
}

/**
 * Run an Apple Calendar sync for a specific project.
 * Exported so ProjectCalendarSettingsScreen can trigger "Sync Now".
 */
export async function runAppleCalendarSync(
  projectId: string,
  fullSync = false,
): Promise<{ synced: number; skipped: boolean }> {
  if (Platform.OS !== 'ios') return { synced: 0, skipped: true };

  let Calendars: typeof import('expo-calendar') | null = null;
  try {
    Calendars = require('expo-calendar') as typeof import('expo-calendar');
  } catch {
    return { synced: 0, skipped: true };
  }

  // Check permission
  const { status } = await Calendars.getCalendarPermissionsAsync();
  if (status !== 'granted') return { synced: 0, skipped: true };

  // Determine delta window
  const lastSyncKey = APPLE_CAL_LAST_SYNC_KEY(projectId);
  const lastSyncStr = fullSync ? null : await AsyncStorage.getItem(lastSyncKey);
  const lastSync = lastSyncStr ? new Date(lastSyncStr) : null;

  // Fetch all calendars and get events in a ±90 day window
  const now = new Date();
  const windowStart = new Date(now.getFullYear(), now.getMonth() - 3, now.getDate());
  const windowEnd = new Date(now.getFullYear(), now.getMonth() + 6, now.getDate());

  const allCalendars = await Calendars.getCalendarsAsync(Calendars.EntityTypes.EVENT);

  // Honor the user's per-project calendar picker. If the selection key is
  // missing we sync everything (back-compat), if it's an empty array we sync
  // nothing, otherwise we intersect with the device-side calendar list in
  // case an ID disappeared (e.g. a shared iCloud calendar was unsubscribed).
  let selectedIds: string[] | null = null;
  try {
    const raw = await AsyncStorage.getItem(APPLE_CAL_SELECTED_IDS_KEY(projectId));
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        selectedIds = parsed.filter((x): x is string => typeof x === 'string');
      }
    }
  } catch (err) {
    debug.warn('Failed to read Apple Calendar selection', err);
  }

  const syncCalendars = selectedIds
    ? allCalendars.filter(c => selectedIds!.includes(c.id))
    : allCalendars;

  if (syncCalendars.length === 0) {
    debug.log('calendar', 'Apple Calendar sync: no calendars selected', { projectId });
    return { synced: 0, skipped: false };
  }

  const events = await Calendars.getEventsAsync(
    syncCalendars.map(c => c.id),
    windowStart,
    windowEnd,
  );

  // Delta filter: skip events that haven't changed since last sync
  const toSync = lastSync
    ? events.filter(ev => {
        if (!ev.lastModifiedDate) return true;
        return new Date(ev.lastModifiedDate) > lastSync;
      })
    : events;

  if (toSync.length === 0) {
    debug.log('calendar', 'Apple Calendar sync: no new/changed events', { projectId });
    return { synced: 0, skipped: false };
  }

  // Map EventKit events to our schema
  const mapped = toSync.map(ev => ({
    id: ev.id,
    title: ev.title || '',
    startDate: ev.startDate ? new Date(ev.startDate).toISOString() : null,
    endDate: ev.endDate ? new Date(ev.endDate).toISOString() : null,
    allDay: ev.allDay ?? false,
    location: (ev as any).location || null,
    notes: ev.notes || null,
    calendarName: allCalendars.find(c => c.id === ev.calendarId)?.title || null,
    calendarId: ev.calendarId,
    lastModifiedDate: ev.lastModifiedDate ? new Date(ev.lastModifiedDate).toISOString() : null,
  }));

  // Send in chunks of 50
  const CHUNK_SIZE = 50;
  const chunks: typeof mapped[] = [];
  for (let i = 0; i < mapped.length; i += CHUNK_SIZE) {
    chunks.push(mapped.slice(i, i + CHUNK_SIZE));
  }

  let totalSynced = 0;
  for (let i = 0; i < chunks.length; i++) {
    try {
      const res = await (store.dispatch as any)(
        syncAppleCalendarChunk(projectId, chunks[i], i + 1, chunks.length),
      );
      totalSynced += res?.synced ?? 0;
    } catch (err) {
      debug.warn('Apple Calendar chunk sync failed', { chunk: i + 1, err });
    }
  }

  // Record last sync timestamp
  await AsyncStorage.setItem(lastSyncKey, now.toISOString());

  // Fire a local notification so the user knows the background sync ran
  if (totalSynced > 0) {
    try {
      await Notifications.scheduleNotificationAsync({
        content: {
          title: 'Apple Calendar synced',
          body: `${totalSynced} event${totalSynced !== 1 ? 's' : ''} updated`,
        },
        trigger: null,
      });
    } catch {
      // Non-critical — user may have denied notification permission
    }
  }

  debug.log('calendar', 'Apple Calendar sync complete', { projectId, totalSynced, chunks: chunks.length });
  return { synced: totalSynced, skipped: false };
}

// Internal action-style helper used above (not exported — call syncAppleCalendar from actions)
function syncAppleCalendarChunk(
  projectId: string,
  events: any[],
  chunk: number,
  totalChunks: number,
) {
  return async (_dispatch: any): Promise<any> => {
    const { apiPost } = require('../utilities/apiClient');
    const state = store.getState();
    const accountId = (state as any).session?.account?.account_id as string;
    const cognitoId = (state as any).authentication?.user?.cognito_id as string;
    if (!accountId || !cognitoId) return { synced: 0 };
    return apiPost(`/calendar/apple-sync/${projectId}/${accountId}/${cognitoId}`, {
      events,
      chunk,
      totalChunks,
    });
  };
}
